// What the two pages share: the map setup, the basemaps, and the small
// formatting helpers.

import * as maplibregl from "maplibre-gl";
import type { StyleSpecification, LngLatBoundsLike } from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "./style.css";

// MapLibre resolves its worker relative to its own module URL, which a bundled
// build does not emit, and the worker in turn imports a sibling shared chunk.
// Left alone, the production build serves a worker that dies on that import and
// the map hangs forever with no error: sources never finish loading and nothing
// draws. The `?worker&url` suffix has Vite bundle the worker with its
// dependencies and hand back a URL that actually runs.
import workerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";

maplibregl.setWorkerUrl(workerUrl);

export const HOME_BOUNDS: LngLatBoundsLike = [
  [-79.1, 39.4],
  [-74.7, 42.8],
];

// Raster basemaps, so the map has no dependency on a remote style document or a
// glyph server. Satellite matters here: it is the only way to actually see the
// islands we camp on.
export const BASEMAPS = {
  topo: {
    tiles: [
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Topo_Map/MapServer/tile/{z}/{y}/{x}",
    ],
    attribution: "Tiles &copy; Esri — USGS, NOAA, OpenStreetMap contributors",
  },
  satellite: {
    tiles: [
      "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
    ],
    attribution: "Imagery &copy; Esri, Maxar, USDA FSA, USGS",
  },
  street: {
    tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
    attribution: "&copy; OpenStreetMap contributors",
  },
} as const;

export type BasemapId = keyof typeof BASEMAPS;

export const WAYPOINT_COLORS: Record<string, string> = {
  campsite: "#f0c46a",
  camp: "#f0c46a",
  "put-in": "#e4ecea",
  "take-out": "#e4ecea",
  launch: "#e4ecea",
  island: "#8ca9b2",
  town: "#e2701e",
  access: "#64c4d8",
  portage: "#d93b3b",
  dam: "#d93b3b",
  hazard: "#d93b3b",
  food: "#3e8e7e",
  lunch: "#3e8e7e",
  landmark: "#9a8b6e",
};
export const WAYPOINT_FALLBACK = "#8ca9b2";

export const json = <T,>(path: string): Promise<T> =>
  fetch(path).then((r) => {
    if (!r.ok) throw new Error(`${path}: ${r.status}`);
    return r.json() as Promise<T>;
  });

export const fmtMiles = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 1 });

export function boundsOf(coords: GeoJSON.Position[]): maplibregl.LngLatBounds {
  const first = coords[0] as [number, number];
  return coords.reduce(
    (b, c) => b.extend(c as [number, number]),
    new maplibregl.LngLatBounds(first, first),
  );
}

export const el = <T extends HTMLElement>(id: string): T => {
  const node = document.getElementById(id);
  if (!node) throw new Error(`missing #${id}`);
  return node as T;
};

export function escapeHtml(value: unknown): string {
  return String(value ?? "").replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]!,
  );
}

export function buildStyle(): StyleSpecification {
  const sources: StyleSpecification["sources"] = {};
  const layers: StyleSpecification["layers"] = [];
  for (const [id, cfg] of Object.entries(BASEMAPS)) {
    sources[`basemap-${id}`] = {
      type: "raster",
      tiles: [...cfg.tiles],
      tileSize: 256,
      maxzoom: 19,
      attribution: cfg.attribution,
    };
    layers.push({
      id: `basemap-${id}`,
      type: "raster",
      source: `basemap-${id}`,
      layout: { visibility: id === "topo" ? "visible" : "none" },
    });
  }
  return { version: 8, sources, layers };
}

export const branchLabel = (id: string) =>
  id === "main" ? "Main stem" : id === "west-branch" ? "West Branch" : id;

export const shortGoalName = (name: string) =>
  name
    .replace("Every mile in Pennsylvania", "PA")
    .replace("The 444 Club", "444")
    .replace("West Branch Paddle Club", "West Branch")
    .replace("The whole Susquehanna", "Whole river");


export const fmtDrive = (minutes: number) =>
  `${Math.floor(minutes / 60)}h${String(minutes % 60).padStart(2, "0")}`;

/** Wires the Topo / Satellite / Street buttons to the basemap layers. */
export function bindBasemapSwitch(map: maplibregl.Map) {
  let active: BasemapId = "topo";
  const buttons = document.querySelectorAll<HTMLButtonElement>("#basemap-switch button");
  for (const btn of buttons) {
    btn.addEventListener("click", () => {
      const pick = btn.dataset.basemap as BasemapId;
      if (!BASEMAPS[pick] || pick === active) return;
      active = pick;
      for (const id of Object.keys(BASEMAPS) as BasemapId[]) {
        map.setLayoutProperty(`basemap-${id}`, "visibility", id === pick ? "visible" : "none");
      }
      for (const b of buttons) b.setAttribute("aria-pressed", String(b === btn));
    });
  }
}
